import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ChevronDownIcon, 
  ChevronUpIcon,
  TvIcon,
  WifiIcon,
  ArrowPathIcon,
  PowerIcon,
  Cog6ToothIcon
} from '@heroicons/react/24/outline';

const TVTroubleshooting: React.FC = () => {
  const [expandedStep, setExpandedStep] = useState<number | null>(null);

  const toggleStep = (stepId: number) => {
    setExpandedStep(expandedStep === stepId ? null : stepId);
  };

  const troubleshootingSteps = [
    {
      id: 1,
      icon: PowerIcon,
      title: 'Power Cycle Your Smart TV',
      summary: 'Modern TVs have memory like computers and can glitch',
      steps: [
        'Turn off your TV using the remote or power button',
        'Unplug the TV from the wall outlet (not just the power strip)',
        'Wait for 60 seconds - this allows the TV\'s memory to fully clear',
        'Press and hold the TV\'s power button for 10 seconds while unplugged (drains residual power)',
        'Plug the TV back into the wall outlet',
        'Turn on the TV and wait for it to fully boot up',
        'Try connecting to WiFi again'
      ],
      explanation: 'Just like computers, smart TVs have RAM and cache memory that can become corrupted or overloaded. A full power cycle clears this memory and often resolves connectivity issues.'
    },
    {
      id: 2,
      icon: WifiIcon,
      title: 'Forget and Reconnect to WiFi Network',
      summary: 'Clear saved network settings and reconnect fresh',
      steps: [
        'Go to your TV\'s Settings menu',
        'Navigate to Network or WiFi settings',
        'Find your current WiFi network in the list',
        'Select "Forget Network" or "Remove Network"',
        'Restart your TV',
        'Go back to Network settings',
        'Select your WiFi network from the available networks',
        'Enter your WiFi password carefully',
        'Wait for the connection to establish'
      ],
      explanation: 'Saved network credentials can become corrupted. Forgetting and re-adding the network creates a fresh connection profile.'
    },
    {
      id: 3,
      icon: ArrowPathIcon,
      title: 'Clear TV Cache and Data',
      summary: 'Remove temporary files that may cause issues',
      steps: [
        'Go to Settings on your TV',
        'Navigate to Apps or Application Manager',
        'Find and select your streaming apps (Netflix, YouTube, etc.)',
        'Select "Clear Cache" for each app',
        'If issues persist, also select "Clear Data" (you\'ll need to log in again)',
        'Restart your TV',
        'For some TVs: Settings > Storage > Clear Cache (system-wide)'
      ],
      explanation: 'Streaming apps and system cache can accumulate corrupted data over time, affecting network performance and stability.'
    },
    {
      id: 4,
      icon: Cog6ToothIcon,
      title: 'Reset Network Settings',
      summary: 'Reset all network configurations to factory defaults',
      steps: [
        'Go to Settings on your TV',
        'Navigate to Network or General settings',
        'Look for "Reset Network Settings" or "Network Reset"',
        'Confirm the reset (this won\'t delete your apps or personal data)',
        'Wait for the TV to restart',
        'Set up your WiFi connection from scratch',
        'Test your connection'
      ],
      explanation: 'Network settings can become misconfigured. A network reset restores default network configurations without affecting your other TV settings.'
    },
    {
      id: 5,
      icon: TvIcon,
      title: 'Update TV Software',
      summary: 'Ensure your TV has the latest firmware',
      steps: [
        'Connect your TV to the internet (use ethernet cable if WiFi isn\'t working)',
        'Go to Settings > Support or System',
        'Select "Software Update" or "Check for Updates"',
        'If an update is available, select "Update Now"',
        'Do not turn off the TV during the update',
        'After update completes, TV will restart automatically',
        'Try connecting to WiFi again'
      ],
      explanation: 'Outdated firmware can cause compatibility issues with modern routers and WiFi standards. Updates often include bug fixes for connectivity problems.'
    },
    {
      id: 6,
      icon: WifiIcon,
      title: 'Check WiFi Band and Channel',
      summary: 'Optimize your TV\'s WiFi connection settings',
      steps: [
        'Check if your TV supports both 2.4 GHz and 5 GHz WiFi',
        'Try connecting to the 2.4 GHz network (better range, more stable)',
        'If using 5 GHz, make sure your TV is close enough to the router',
        'In router settings, try changing the WiFi channel to avoid interference',
        'Disable "WiFi 6" or "AX mode" on router if TV is older',
        'Enable "Legacy Mode" or "Mixed Mode" on your router',
        'Restart both TV and router after making changes'
      ],
      explanation: 'Older smart TVs may not be compatible with newer WiFi standards or certain channels. Using 2.4 GHz provides better range and compatibility.'
    }
  ];

  const getColorClasses = () => {
    return { bg: 'bg-white', border: 'border-gray-200', icon: 'text-airmax-blue', text: 'text-gray-700' };
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-6 md:p-8">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="flex justify-center mb-4">
          <div className="bg-airmax-blue p-4 rounded-full">
            <TvIcon className="h-12 w-12 text-white" />
          </div>
        </div>
        <h2 className="text-3xl font-bold text-gray-900 mb-3">Smart TV WiFi Troubleshooting</h2>
        <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-4">
          Modern smart TVs are essentially computers with memory, processors, and software. Like any computer, they can experience glitches, memory issues, and connectivity problems.
        </p>
        <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-lg max-w-3xl mx-auto">
          <p className="text-sm text-gray-700">
            <strong>Important:</strong> Try these steps in order. Most TV WiFi issues can be resolved with a simple power cycle (Step 1).
          </p>
        </div>
      </div>

      {/* Troubleshooting Steps */}
      <div className="space-y-4">
        {troubleshootingSteps.map((step, index) => {
          const Icon = step.icon;
          const isExpanded = expandedStep === step.id;

          const colors = getColorClasses();

          return (
            <motion.div
              key={step.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className={`border-2 ${colors.border} rounded-lg overflow-hidden`}
            >
              {/* Step Header */}
              <button
                onClick={() => toggleStep(step.id)}
                className={`w-full ${colors.bg} p-4 md:p-6 flex items-center justify-between hover:opacity-90 transition-opacity`}
              >
                <div className="flex items-center space-x-4 flex-1">
                  <div className={`${colors.icon} flex-shrink-0`}>
                    <Icon className="h-8 w-8" />
                  </div>
                  <div className="text-left flex-1">
                    <h3 className="text-lg md:text-xl font-bold text-gray-900">
                      Step {step.id}: {step.title}
                    </h3>
                    <p className="text-sm text-gray-600 mt-1">{step.summary}</p>
                  </div>
                </div>
                <div className="ml-4 flex-shrink-0">
                  {isExpanded ? (
                    <ChevronUpIcon className="h-6 w-6 text-gray-600" />
                  ) : (
                    <ChevronDownIcon className="h-6 w-6 text-gray-600" />
                  )}
                </div>
              </button>

              {/* Step Details */}
              <AnimatePresence>
                {isExpanded && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="bg-white border-t-2 border-gray-100"
                  >
                    <div className="p-4 md:p-6">
                      {/* Explanation */}
                      <div className="bg-gray-50 rounded-lg p-4 mb-4 border border-gray-200">
                        <p className="text-sm text-gray-700 font-medium">
                          <strong>Why this works:</strong> {step.explanation}
                        </p>
                      </div>

                      {/* Steps */}
                      <h4 className="font-bold text-gray-900 mb-3">Follow these steps:</h4>
                      <ol className="space-y-3">
                        {step.steps.map((instruction, idx) => (
                          <li key={idx} className="flex items-start">
                            <span className={`${colors.icon} font-bold mr-3 flex-shrink-0`}>
                              {idx + 1}.
                            </span>
                            <span className="text-gray-700">{instruction}</span>
                          </li>
                        ))}
                      </ol>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          );
        })}
      </div>

      {/* Footer Note */}
      <div className="mt-8 bg-gradient-to-r from-yellow-50 to-orange-50 border-2 border-yellow-400 rounded-lg p-6">
        <h3 className="font-bold text-gray-900 mb-2 flex items-center">
          <span className="text-2xl mr-2">💡</span>
          Still Having Issues?
        </h3>
        <p className="text-gray-700 mb-3">
          If you've tried all these steps and your TV still won't connect to WiFi, the issue may be with your router or internet service. Try these additional steps:
        </p>
        <ul className="space-y-2 text-gray-700">
          <li className="flex items-start">
            <span className="mr-2">•</span>
            <span>Restart your router (see our Router Troubleshooting section above)</span>
          </li>
          <li className="flex items-start">
            <span className="mr-2">•</span>
            <span>Try connecting another device to verify your WiFi is working</span>
          </li>
          <li className="flex items-start">
            <span className="mr-2">•</span>
            <span>Use an Ethernet cable to connect your TV directly to the router</span>
          </li>
          <li className="flex items-start">
            <span className="mr-2">•</span>
            <span>Contact us at <a href="tel:928-565-6707" className="text-airmax-blue font-semibold hover:underline">928-565-6707</a> for further assistance</span>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default TVTroubleshooting;
